import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PartyPopper, Send, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * PassedStoryPrompt — optional, learner-owned post-pass quote prompt.
 *
 * Mounts silently on the Learner hub; only renders when the backend has
 * an eligible pending prompt (24h+ after a verified pass, no story/skip
 * on file). The learner can submit, skip, or dismiss — never forced.
 */
export default function PassedStoryPrompt() {
  const [prompt, setPrompt] = useState(null);
  const [quote, setQuote] = useState("");
  const [anon, setAnon] = useState(false);
  const [consent, setConsent] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/api/learner-stories/prompt`, { withCredentials: true });
      if (data?.prompt) setPrompt(data.prompt);
    } catch { /* silent — prompt is optional */ }
  };
  useEffect(() => { load(); }, []);

  if (!prompt || dismissed) return null;

  const submit = async () => {
    if (quote.trim().length < 10) {
      toast.error("Add a short sentence (at least 10 characters).");
      return;
    }
    if (!consent) {
      toast.error("Tick the publish-consent box so we know you're happy to share.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/api/learner-stories/submit`, {
        test_result_id: prompt.test_result_id,
        quote: quote.trim(),
        is_anonymous: anon,
        approved_to_publish: true,
      }, { withCredentials: true });
      toast.success("Thanks — your story is with our team for a quick moderation check.");
      setPrompt(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save your story");
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    try {
      await axios.post(`${API}/api/learner-stories/skip/${prompt.test_result_id}`, {}, { withCredentials: true });
      setPrompt(null);
    } catch {
      setDismissed(true);
    }
  };

  return (
    <div data-testid="passed-story-prompt" className="rounded-2xl bg-gradient-to-br from-emerald-500/10 via-teal-500/10 to-sky-500/10 border border-emerald-300/60 dark:border-emerald-700/50 p-4 sm:p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0">
          <PartyPopper className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-extrabold text-slate-900 dark:text-white">You passed. When you&apos;re ready, share a sentence.</p>
          <p className="text-[12px] text-slate-600 dark:text-slate-300 mt-0.5">
            Would you like to leave one short sentence about <span className="font-bold">{prompt.instructor_name}</span>? Totally optional — helps future learners pick with confidence.
          </p>

          <textarea
            data-testid="story-quote"
            value={quote}
            onChange={(e) => setQuote(e.target.value.slice(0, 240))}
            rows={3}
            placeholder={`e.g. "${prompt.instructor_name} kept me calm on every lesson and made me believe I could do it."`}
            className="mt-3 w-full px-3 py-2 text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 dark:text-white resize-none"
          />
          <p className="mt-1 text-[10px] text-slate-400 text-right">{quote.length}/240</p>

          <div className="mt-1 space-y-1">
            <label className="flex items-center gap-2 text-[12px] text-slate-700 dark:text-slate-300">
              <input data-testid="story-anonymous" type="checkbox" checked={anon} onChange={(e) => setAnon(e.target.checked)} className="w-4 h-4 rounded" />
              Post as Anonymous (instead of my first name)
            </label>
            <label className="flex items-center gap-2 text-[12px] text-slate-700 dark:text-slate-300">
              <input data-testid="story-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="w-4 h-4 rounded" />
              I'm happy for this to appear publicly on my instructor's profile.
            </label>
          </div>

          <div className="mt-3 flex gap-2">
            <button
              data-testid="story-submit"
              onClick={submit}
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2.5 text-xs font-extrabold rounded-xl bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-60"
            >
              <Send className="w-3.5 h-3.5" /> Share my story
            </button>
            <button
              data-testid="story-skip"
              onClick={skip}
              disabled={saving}
              className="px-3 py-2.5 text-xs font-bold rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200"
            >
              No thanks
            </button>
          </div>
        </div>
        <button
          data-testid="story-dismiss"
          onClick={() => setDismissed(true)}
          aria-label="Dismiss"
          className="p-1 rounded-full text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
